import React, { useState, useEffect, useMemo } from 'react'
import { formatCurrency, formatQuantity } from '../../utils/formatters'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Box,
  IconButton,
  DialogContentText
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import BlockIcon from '@mui/icons-material/Block'
import { billService } from '../../services/bills/billService'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'

interface Bill {
  _id: string
  bill_no: string
  customer_name: string
  area_name: string
  quantity_ctn: number
  quantity_pcs: number
  amount: number
  discount: number
  adv_tax: number
  date: string
}

interface BillSelectionModalProps {
  open: boolean
  onClose: () => void
  bookerName: string
  bookerId: string
  bills: Bill[]
  selectedBillIds: string[]
  onConfirm: (bookerId: string, selectedIds: string[]) => void
  onBillCancelled: (billId: string) => void
}

const BillSelectionModal: React.FC<BillSelectionModalProps> = ({
  open,
  onClose,
  bookerName,
  bookerId,
  bills,
  selectedBillIds,
  onConfirm,
  onBillCancelled
}) => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const [localSelectedIds, setLocalSelectedIds] = useState<Set<string>>(new Set(selectedBillIds))
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)
  const [billToCancel, setBillToCancel] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setLocalSelectedIds(new Set(selectedBillIds))
    }
  }, [open, selectedBillIds])

  const handleToggleBill = (billId: string) => {
    const newSelected = new Set(localSelectedIds)
    if (newSelected.has(billId)) {
      newSelected.delete(billId)
    } else {
      newSelected.add(billId)
    }
    setLocalSelectedIds(newSelected)
  }

  const handleToggleAll = () => {
    if (localSelectedIds.size === bills.length) {
      setLocalSelectedIds(new Set())
    } else {
      setLocalSelectedIds(new Set(bills.map(b => b._id)))
    }
  }

  const handleConfirm = () => {
    onConfirm(bookerId, Array.from(localSelectedIds))
    onClose()
  }

  const summary = useMemo(() => {
    let count = 0
    let amount = 0
    let totalCtn = 0
    const ids = localSelectedIds

    bills.forEach(bill => {
      if (ids.has(bill._id)) {
        count++
        amount += bill.amount
        totalCtn += bill.quantity_ctn
      }
    })

    return { count, amount, totalCtn }
  }, [localSelectedIds, bills])

  const handleCancelClick = (billId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setBillToCancel(billId)
    setConfirmCancelOpen(true)
  }

  const confirmCancelBill = async () => {
    if (billToCancel) {
        try {
            await billService.cancelBill(billToCancel)
            onBillCancelled(billToCancel)
            
            // Also remove from local selection if present
            if (localSelectedIds.has(billToCancel)) {
                const newSelected = new Set(localSelectedIds)
                newSelected.delete(billToCancel)
                setLocalSelectedIds(newSelected)
            }
        } catch (error) {
            console.error("Failed to cancel bill", error)
            alert("Failed to cancel bill")
        } finally {
            setConfirmCancelOpen(false)
            setBillToCancel(null)
        }
    }
  }

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Select Bills for {bookerName}</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={localSelectedIds.size > 0 && localSelectedIds.size < bills.length}
                    checked={bills.length > 0 && localSelectedIds.size === bills.length}
                    onChange={handleToggleAll}
                  />
                </TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Area</TableCell>
                <TableCell>{uom.plural}</TableCell>
                <TableCell align="right">Disc</TableCell>
                <TableCell align="right">Adv Tax</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bills.map((bill) => {
                const isSelected = localSelectedIds.has(bill._id)
                return (
                  <TableRow
                    key={bill._id}
                    hover
                    onClick={() => handleToggleBill(bill._id)}
                    role="checkbox"
                    aria-checked={isSelected}
                    selected={isSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isSelected} />
                    </TableCell>
                    <TableCell>{bill.customer_name}</TableCell>
                    <TableCell>{bill.area_name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                         <Typography variant="body2" fontWeight="bold">
                          {formatQuantity(bill.quantity_ctn)}
                        </Typography>
                        {bill.quantity_pcs > 0 && (
                          <Typography variant="caption" color="text.secondary">
                             - {formatQuantity(bill.quantity_pcs)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                        {bill.discount > 0 ? formatQuantity(bill.discount) : '-'}
                    </TableCell>
                    <TableCell align="right">
                        {bill.adv_tax > 0 ? formatQuantity(bill.adv_tax) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      Rs {formatCurrency(bill.amount)}
                    </TableCell>
                    <TableCell align="center">
                        <IconButton 
                            size="small" 
                            color="error" 
                            onClick={(e) => handleCancelClick(bill._id, e)}
                            title="Cancel Bill"
                        >
                            <BlockIcon fontSize="small" />
                        </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Box sx={{ pl: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Selected: <span style={{ color: '#000', fontWeight: 'bold' }}>{summary.count} Bills</span>, <span style={{ color: '#000', fontWeight: 'bold' }}>{formatQuantity(summary.totalCtn)} Cts</span> (Rs {formatCurrency(summary.amount)})
          </Typography>
        </Box>
        <Box>
            <Button onClick={onClose} color="inherit" sx={{ mr: 1 }}>
            Cancel
            </Button>
            <Button onClick={handleConfirm} variant="contained" disabled={summary.count === 0}>
            Confirm Selection
            </Button>
        </Box>
      </DialogActions>
    </Dialog>

    <Dialog
        open={confirmCancelOpen}
        onClose={() => setConfirmCancelOpen(false)}
    >
        <DialogTitle>Confirm Cancellation</DialogTitle>
        <DialogContent>
            <DialogContentText>
                Are you sure you want to cancel this bill? This action will set the bill status to 'Cancelled' and remove it from the list.
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setConfirmCancelOpen(false)}>No, Keep it</Button>
            <Button onClick={confirmCancelBill} color="error" autoFocus>
                Yes, Cancel Bill
            </Button>
        </DialogActions>
    </Dialog>
    </>
  )
}

export default BillSelectionModal
